import { fetchJsonApi } from "../apiClient";

/**
 * Direct-to-storage admin upload (PR-1.5), one file at a time:
 *
 *   1. POST /internal/uploads {filename, contentType, size, metadata}
 *        -> {uploadId, url, method, headers, fields, key, maxBytes}
 *   2. Send the file straight to storage with the returned method/headers/fields
 *      (Azure: PUT with x-ms-blob-type; S3 later: multipart POST with policy fields).
 *   3. Poll GET /internal/uploads/{uploadId} until READY or FAILED.
 *
 * Storage hosts must be allowed by the CSP connect-src (public/staticwebapp.config.json).
 */

export const UPLOAD_STATUS = Object.freeze({
  QUEUED: "queued",
  UPLOADING: "uploading",
  PROCESSING: "processing",
  READY: "ready",
  FAILED: "failed",
});

export const POLL_DEFAULTS = Object.freeze({
  intervalMs: 2000,
  maxIntervalMs: 10000,
  backoff: 1.5,
  timeoutMs: 10 * 60 * 1000,
  maxConsecutiveErrors: 3,
});

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Step 1: ask the API for an upload target. `authHeaders` = { Authorization: "Bearer ..." }. */
export function requestUploadTarget(file, metadata, authHeaders) {
  return fetchJsonApi("/internal/uploads", {
    method: "POST",
    headers: { ...authHeaders, "Content-Type": "application/json" },
    body: JSON.stringify({
      filename: file.name,
      contentType: file.type || "",
      size: file.size,
      metadata: metadata || {},
    }),
  });
}

/**
 * Step 2: send the file to storage with progress (fetch has no upload progress, so XHR).
 * Resolves on 2xx; rejects with a readable Error otherwise.
 */
export function sendFileToStorage(target, file, onProgress, XhrImpl) {
  const Xhr = XhrImpl || window.XMLHttpRequest;
  return new Promise((resolve, reject) => {
    const xhr = new Xhr();
    const method = (target.method || "PUT").toUpperCase();
    xhr.open(method, target.url);

    let body = file;
    if (method === "POST") {
      const form = new FormData();
      Object.entries(target.fields || {}).forEach(([name, value]) => form.append(name, value));
      form.append("file", file);
      body = form;
    } else {
      Object.entries(target.headers || {}).forEach(([name, value]) =>
        xhr.setRequestHeader(name, value)
      );
    }

    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
        }
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress) onProgress(100);
        resolve();
      } else {
        reject(new Error(`Storage rejected the upload (HTTP ${xhr.status}).`));
      }
    };
    xhr.onerror = () =>
      reject(new Error("Network error while uploading to storage (check connection, CORS and CSP)."));
    xhr.onabort = () => reject(new Error("Upload was cancelled."));
    xhr.send(body);
  });
}

/**
 * Step 3: poll the upload status until READY/FAILED. The first check is immediate, then every
 * `intervalMs` growing by `backoff` up to `maxIntervalMs`; gives up after `timeoutMs`.
 * `getAuthHeaders` is called per request so long waits keep a fresh token.
 */
export async function pollUploadStatus(uploadId, getAuthHeaders, options = {}) {
  const opts = { ...POLL_DEFAULTS, ...options };
  const sleep = opts.sleep || defaultSleep;
  const now = opts.now || Date.now;
  const deadline = now() + opts.timeoutMs;
  let delay = opts.intervalMs;
  let consecutiveErrors = 0;

  for (;;) {
    try {
      const headers = await getAuthHeaders();
      if (!headers) return null; // sign-in redirect started
      const data = await fetchJsonApi(`/internal/uploads/${encodeURIComponent(uploadId)}`, {
        headers,
      });
      consecutiveErrors = 0;
      if (data && (data.status === "READY" || data.status === "FAILED")) {
        return data;
      }
    } catch (err) {
      consecutiveErrors += 1;
      if (consecutiveErrors >= opts.maxConsecutiveErrors) throw err;
    }
    if (now() + delay > deadline) {
      throw new Error("Timed out waiting for processing. Check the gallery later.");
    }
    await sleep(delay);
    delay = Math.min(Math.round(delay * opts.backoff), opts.maxIntervalMs);
  }
}

/**
 * Upload one file and wait for ingestion. `onUpdate(patch)` receives
 * `{status, progress?, message?, uploadId?, mediaId?}`. Never throws: failures are reported as
 * `{status: "failed", message}`. Returns the final patch, or null if a sign-in redirect started.
 */
export async function uploadFileDirect({ file, metadata, getAuthHeaders, onUpdate, options = {} }) {
  const update = onUpdate || (() => {});
  try {
    update({ status: UPLOAD_STATUS.UPLOADING, progress: 0, message: "" });
    const headers = await getAuthHeaders();
    if (!headers) return null;
    const target = await requestUploadTarget(file, metadata, headers);
    update({ status: UPLOAD_STATUS.UPLOADING, progress: 0, uploadId: target.uploadId });

    await sendFileToStorage(
      target,
      file,
      (progress) => update({ status: UPLOAD_STATUS.UPLOADING, progress }),
      options.XhrImpl
    );
    update({ status: UPLOAD_STATUS.PROCESSING, progress: 100 });

    const result = await pollUploadStatus(target.uploadId, getAuthHeaders, options.poll);
    if (result === null) return null;
    const final =
      result.status === "READY"
        ? { status: UPLOAD_STATUS.READY, mediaId: result.mediaId, message: "" }
        : { status: UPLOAD_STATUS.FAILED, message: result.error || "Processing failed." };
    update(final);
    return final;
  } catch (err) {
    const final = { status: UPLOAD_STATUS.FAILED, message: err?.message || "Upload failed." };
    update(final);
    return final;
  }
}
